import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Loading from 'components/Loading';
import { BudgetExpenseOrIncome } from 'business/constants';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import Flex from 'ui/Flex/Flex';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    ALL_AP_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import TabActions from '../TabActions';
import { useApsQuery } from './hooks';
import APAEGrid from './APAEGrid';
import { ApT, INVESTMENT_OPERATING, NAME_FIELD } from './types';

const StyAgContainer = styled('div', {
    name: 'StyAgContainer',
})({
    width: '100%',
    height: '100%',
    '&& .ag-root-wrapper': {
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
});

const StyGridView = styled('div', {
    name: 'StyGridView',
})({
    '& span.ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
});

export default function AllAps({ year }: { year: string }): React.ReactElement {
    const { t } = useTranslation();

    const { tab, expenseOrIncome = BudgetExpenseOrIncome.EXPENSE } =
        useParams();

    const expenseOrIncomeEnum =
        BudgetExpenseOrIncome[
            expenseOrIncome.toUpperCase() as keyof typeof BudgetExpenseOrIncome
        ] ?? BudgetExpenseOrIncome.INCOME;
    const referenceYear: number = parseInt(year, 10);

    const { data, isLoading: areApsLoading } = useApsQuery({
        year: referenceYear,
        expenseOrIncome: expenseOrIncomeEnum,
    });

    const queryKeyToRefresh = [
        'allApsView',
        expenseOrIncome.toUpperCase(),
        referenceYear,
    ];
    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: `/budget_overview/${tab}/expense`,
        },
        {
            label: t('budget.income'),
            pathname: `/budget_overview/${tab}/income`,
        },
    ].map((tabItem) => ({
        ...tabItem,
        key: tabItem.label,
        tabActions: !areApsLoading && (
            <TabActions
                queryToRefresh={queryKeyToRefresh}
                hideEmpty={false}
                trackedEvents={TRACKABLE_EVENTS.budget.budgetOverview.aps}
                toggleLabel={t('budgetProposal.hideEmptyAps')}
                fileName={getExcelFilename(ALL_AP_FILE_PREFIX, [
                    expenseOrIncome,
                ])}
            />
        ),
        children:
            areApsLoading || !data ? (
                <Loading />
            ) : (
                <Flex direction="column">
                    <StyAgContainer className="ag-theme-material">
                        <APAEGrid
                            data={data
                                .filter(
                                    (key: ApT) =>
                                        key[INVESTMENT_OPERATING] ===
                                        'INVESTMENT',
                                )
                                .sort((a: ApT, b: ApT) =>
                                    a[NAME_FIELD].localeCompare(b[NAME_FIELD]),
                                )}
                            targetYear={referenceYear}
                            isAP
                            excelExport={{
                                fileName: getExcelFilename(ALL_AP_FILE_PREFIX, [
                                    expenseOrIncome,
                                ]),
                                sheetName: 'APs',
                            }}
                        />
                    </StyAgContainer>
                    <StyAgContainer className="ag-theme-material">
                        <APAEGrid
                            data={data
                                .filter(
                                    (key: ApT) =>
                                        key[INVESTMENT_OPERATING] ===
                                        'OPERATING',
                                )
                                .sort((a: ApT, b: ApT) =>
                                    a[NAME_FIELD].localeCompare(b[NAME_FIELD]),
                                )}
                            targetYear={referenceYear}
                            excelExport={{
                                fileName: getExcelFilename(ALL_AP_FILE_PREFIX, [
                                    expenseOrIncome,
                                ]),
                                sheetName: 'AEs',
                            }}
                        />
                    </StyAgContainer>
                </Flex>
            ),
        disabled: false,
    }));

    return (
        <Box sx={{ m: '0rem 3rem' }}>
            <StyGridView>
                <AuxiliaryTabs tabsList={tabsList} />
            </StyGridView>
        </Box>
    );
}
